import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FolderOpen, ChevronRight, ChevronLeft, Eye, Search, Filter,
  Plus, Loader2, FileSpreadsheet, Edit, Trash2, ChevronUp, ChevronDown
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_DOCS = `${process.env.REACT_APP_BACKEND_URL}/api/docs-v2`;

const StatusBadge = ({ status }) => {
  const statusConfig = {
    nuevo: { label: "Nuevo", className: "bg-blue-100 text-blue-800" },
    en_cours: { label: "En Curso", className: "bg-orange-100 text-orange-800" },
    valide: { label: "Validado", className: "bg-green-100 text-green-800" },
    rejete: { label: "Rechazado", className: "bg-red-100 text-red-800" },
    enviado_greenflex: { label: "Enviado Greenflex", className: "bg-emerald-100 text-emerald-800" },
  };
  const config = statusConfig[status] || statusConfig.nuevo;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
};

const DossiersList = ({ isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read filter from URL immediately (no useEffect) to avoid race condition on first fetch
  const initialFilter = new URLSearchParams(location.search).get("filter") || "todos";

  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialFilter);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dossierToDelete, setDossierToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("date_installation");
  const [sortDir, setSortDir] = useState("desc");
  const [totalPages, setTotalPages] = useState(1);
  const [totalDossiers, setTotalDossiers] = useState(0);
  const fetchAbortRef = useRef(null);

  // Update filter when URL changes (navigation between filter links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get("filter") || "todos";
    setStatusFilter(filter);
    setCurrentPage(1);
  }, [location.search]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === dossiers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(dossiers.map(d => d.id));
    }
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecciona al menos un dossier");
      return;
    }
    setExporting(true);
    try {
      const response = await axios.post(`${API_DOCS}/export-excel`, { dossier_ids: selectedIds }, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Dossiers_Export.xlsx';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success(`${selectedIds.length} dossier(s) exportado(s)`);
    } catch (error) {
      toast.error("Error al exportar dossiers");
    } finally {
      setExporting(false);
    }
  };

  const fetchDossiers = useCallback(async () => {
    // Cancel any previous in-flight request
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "todos") params.append("statut", statusFilter);
      if (search) params.append("search", search);
      params.append("page", currentPage);
      params.append("sort_by", sortBy);
      params.append("sort_dir", sortDir);
      params.append("limit", 20);

      const response = await axios.get(`${API}/dossiers?${params}`, { signal: controller.signal });
      setDossiers(response.data.dossiers);
      setTotalPages(response.data.total_pages);
      setTotalDossiers(response.data.total);
    } catch (error) {
      if (axios.isCancel(error) || error?.name === "CanceledError") return;
      toast.error("Error al cargar dossiers");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [search, statusFilter, currentPage, sortBy, sortDir]);

  useEffect(() => {
    fetchDossiers();
  }, [fetchDossiers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const handleDelete = async () => {
    if (!dossierToDelete) return;
    try {
      await axios.delete(`${API}/dossiers/${dossierToDelete.id}`);
      toast.success("Dossier eliminado");
      setDeleteDialogOpen(false);
      fetchDossiers();
    } catch (error) {
      toast.error("Error al eliminar dossier");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Dossiers</h1>
          <p className="text-slate-500 mt-1">Gestión de expedientes CAE</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button
              onClick={handleExport}
              disabled={exporting}
              variant="outline"
              className="border-green-500 text-green-700 hover:bg-green-50"
              data-testid="export-excel-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Exportar ({selectedIds.length})
            </Button>
          )}
          <Button
            onClick={() => navigate("/nuevo")}
            className="bg-slate-900 hover:bg-slate-800 btn-animate"
            data-testid="new-dossier-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Dossier
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por referencia, nombre o DNI..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="nuevo">Nuevos</SelectItem>
                <SelectItem value="en_cours">En Curso</SelectItem>
                <SelectItem value="valide">Validados</SelectItem>
                <SelectItem value="rejete">Rechazados</SelectItem>
                <SelectItem value="enviado_greenflex">Enviado Greenflex</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : dossiers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FolderOpen className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay dossiers</p>
              <Button variant="link" onClick={() => navigate("/nuevo")} className="mt-2">
                Crear primer dossier
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={dossiers.length > 0 && selectedIds.length === dossiers.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => { if (sortBy === "reference") { setSortDir(d => d === "asc" ? "desc" : "asc"); } else { setSortBy("reference"); setSortDir("asc"); } setCurrentPage(1); }}>
                    <span className="flex items-center gap-1">Referencia {sortBy === "reference" && (sortDir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>)}</span>
                  </TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>DNI</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => { if (sortBy === "date_installation") { setSortDir(d => d === "asc" ? "desc" : "asc"); } else { setSortBy("date_installation"); setSortDir("desc"); } setCurrentPage(1); }}>
                    <span className="flex items-center gap-1">Fecha {sortBy === "date_installation" && (sortDir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>)}</span>
                  </TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dossiers.map((dossier) => (
                  <TableRow key={dossier.id} data-testid={`dossier-row-${dossier.id}`} className={selectedIds.includes(dossier.id) ? "bg-green-50/50" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(dossier.id)}
                        onCheckedChange={() => toggleSelect(dossier.id)}
                        data-testid={`select-dossier-${dossier.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <span className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" onClick={() => navigate(`/dossier/${dossier.id}`)}>{dossier.reference}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" onClick={() => navigate(`/dossier/${dossier.id}`)}>{dossier.donnees?.identite?.nom_proprietaire || "-"}</span>
                    </TableCell>
                    <TableCell>{dossier.donnees?.identite?.dni || "-"}</TableCell>
                    <TableCell>
                      <StatusBadge status={dossier.statut} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const d = dossier.date_installation || dossier.date_creation;
                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) {
                          const [day, month, year] = d.split('/');
                          return new Date(year, month - 1, day).toLocaleDateString('es-ES');
                        }
                        return new Date(d).toLocaleDateString('es-ES');
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/dossier/${dossier.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/dossier/${dossier.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {isAdmin && (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setDossierToDelete(dossier);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "..." ? (
                <span key={`dots-${i}`} className="px-2 text-slate-400">...</span>
              ) : (
                <Button key={p} variant={p === currentPage ? "default" : "outline"} size="sm" className="min-w-[36px]" onClick={() => setCurrentPage(p)}>
                  {p}
                </Button>
              )
            )}
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <span className="text-sm text-slate-500 ml-3">{totalDossiers} dossiers</span>
        </div>
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Dossier</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el dossier {dossierToDelete?.reference}?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DossiersList;
